import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flag, Globe2, ShieldCheck, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";

const PILLARS = [
  {
    icon: Flag,
    title: "Born To Run Together",
    copy: "Fitygrity is a running and fitness community first. From your first 5K to your biggest race dream, you never train alone — group runs, virtual challenges and a tribe that shows up for every start line and every finish-line hug.",
  },
  {
    icon: Globe2,
    title: "Travel Is The Reward",
    copy: "We turn races into journeys. Bib, hotel, transfers and a crew of fellow runners — curated expeditions to quality races across India and abroad, so all you do is run.",
  },
  {
    icon: ShieldCheck,
    title: "Every Mile Counts",
    copy: "No honour systems, no screenshots. Fitygrity's virtual events sync straight from your Strava device — GPS-checked, pace-audited and leaderboard-verified — so every kilometre you earn is real.",
  },
];

const STATS = [
  { value: "12K+", label: "Runners United" },
  { value: "29+", label: "Races Curated" },
  { value: "30+", label: "Curated Races" },
  { value: "2026", label: "Founded" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl mb-16"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-6">About Fitygrity</p>
          <h1 data-testid="about-headline" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.08]">
            Building the future of endurance through expeditions, virtual challenges and
            <span className="italic text-[#FF4E00]"> unforgettable athletic gatherings.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#374151] leading-relaxed max-w-2xl">
            Fitygrity began with a simple frustration: runners in India trained hard but raced lonely —
            scattered across apps, WhatsApp groups and ticketing sites. We built one home for all of it:
            verified virtual competition, curated race travel, honest gear advice and a community that
            celebrates every finisher equally.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-20" data-testid="about-stats">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6"
            >
              <div className="font-serif text-3xl text-[#FF4E00]">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mt-2">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="space-y-16 sm:space-y-20">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className={`max-w-3xl flex flex-col sm:flex-row gap-6 sm:gap-12 ${i === 1 ? "lg:ml-auto" : ""}`}
            >
              <span className="font-serif text-6xl sm:text-7xl font-light text-stroke-amber leading-none shrink-0 select-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <p.icon className="w-5 h-5 text-[#FF4E00] mb-4" />
                <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117] tracking-tight mb-4">{p.title}</h2>
                <p className="text-sm sm:text-base text-[#374151] leading-relaxed max-w-xl">{p.copy}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mt-24 rounded-3xl bg-[#0D1117] p-10 sm:p-14 text-center"
        >
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#FBF9F5] tracking-tight">
            The finish line doesn't define you. <span className="italic text-[#FF4E00]">The journey does.</span>
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="group mt-8 inline-flex items-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300"
          >
            Start Your Journey
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
